/**
 * Live header search — debounced, scoped, abortable.
 *
 * Talks to admin-ajax.php (action=lm_live_search) and renders grouped
 * results inside the GLightbox-driven header modal (`#search`).
 *
 * NOTE: GLightbox clones the `#search` element into `.glightbox-container`
 * when the modal opens, so we use delegated listeners on `document` and
 * always operate on the currently visible modal (clone first, fall back to
 * the original element if no clone is mounted).
 */

const DEBOUNCE_MS = 250;

const state = {
    scope: 'alle',
    lastQuery: '',
    timer: null,
    controller: null,
};

function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function highlight(text, query) {
    const safe = escapeHtml(text);
    if (!query) return safe;
    const tokens = query
        .split(/\s+/)
        .filter(Boolean)
        .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!tokens.length) return safe;
    const pattern = new RegExp('(' + tokens.join('|') + ')', 'ig');
    return safe.replace(pattern, '<mark>$1</mark>');
}

function renderGroup(group, query) {
    const items = group.items
        .map((item) => {
            const title = highlight(item.title, query);
            const excerpt = item.excerpt
                ? `<p class="lm-live-search__excerpt">${highlight(item.excerpt, query)}</p>`
                : '';
            const target = item.target === '_blank' ? '_blank' : '_self';
            const relAttr = target === '_blank' ? ' rel="noopener noreferrer"' : '';
            return `
                <li class="lm-live-search__item">
                    <a class="lm-live-search__link no-style" href="${escapeHtml(item.url)}" target="${target}"${relAttr}>
                        <h4 class="lm-live-search__title">${title}</h4>
                        ${excerpt}
                    </a>
                </li>
            `;
        })
        .join('');

    return `
        <section class="lm-live-search__group" data-group="${escapeHtml(group.key)}">
            <h3 class="lm-live-search__group-title">${escapeHtml(group.label)}</h3>
            <ul class="lm-live-search__list">${items}</ul>
        </section>
    `;
}

function render(container, payload, emptyMessage) {
    if (!payload || !payload.groups || !payload.groups.length) {
        container.innerHTML = `<p class="lm-live-search__empty">${escapeHtml(emptyMessage)}</p>`;
        return;
    }
    container.innerHTML = payload.groups.map((group) => renderGroup(group, payload.query)).join('');
}

/**
 * Return the active form: prefer the GLightbox clone if present.
 */
function getActiveForm() {
    const clone = document.querySelector('.glightbox-container .lm-live-search');
    if (clone) return clone;
    return document.querySelector('.lm-live-search');
}

function getModalFrom(form) {
    return form.closest('.header-search-modal') || form.parentElement || document;
}

function syncBadges(modal) {
    const badges = modal.querySelectorAll('.lm-live-search__badge');
    badges.forEach((badge) => {
        const isActive = badge.dataset.scope === state.scope;
        badge.classList.toggle('is-active', isActive);
        badge.setAttribute('aria-checked', isActive ? 'true' : 'false');
    });
}

function clearResults(modal) {
    const results = modal.querySelector('.lm-live-search__results');
    const status = modal.querySelector('.lm-live-search__status');
    if (results) results.innerHTML = '';
    if (status) status.textContent = '';
}

function syncResetButton(input) {
    const form = input.closest('.lm-live-search');
    if (!form) return;
    const reset = form.querySelector('.button--reset');
    if (!reset) return;
    reset.hidden = input.value.length === 0;
}

function resetSearch(input) {
    const form = input.closest('.lm-live-search');
    if (!form) return;
    const modal = getModalFrom(form);
    input.value = '';
    input.focus();
    clearResults(modal);
    state.lastQuery = '';
    syncResetButton(input);
}

function run(query, force) {
    const config = window.lmSearch;
    if (!config) return;

    const form = getActiveForm();
    if (!form) return;
    const modal = getModalFrom(form);
    const results = modal.querySelector('.lm-live-search__results');
    const status = modal.querySelector('.lm-live-search__status');
    if (!results) return;

    const emptyMessage = results.getAttribute('data-empty') || '';
    const minChars = Number(config.minChars) > 0 ? Number(config.minChars) : 2;
    const trimmed = (query || '').trim();

    if (trimmed.length < minChars) {
        clearResults(modal);
        state.lastQuery = '';
        return;
    }
    if (!force && trimmed === state.lastQuery) return;
    state.lastQuery = trimmed;

    if (state.controller) state.controller.abort();
    state.controller = new AbortController();

    if (status) status.textContent = '…';

    const body = new URLSearchParams();
    body.set('action', config.action || 'lm_live_search');
    body.set('q', trimmed);
    body.set('scope', state.scope);
    if (config.lang) {
        body.set('lang', config.lang);
    }

    fetch(config.ajaxurl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: body.toString(),
        signal: state.controller.signal,
    })
        .then((response) => response.json())
        .then((json) => {
            const visibleForm = getActiveForm();
            const visibleModal = visibleForm ? getModalFrom(visibleForm) : modal;
            const visibleResults = visibleModal.querySelector('.lm-live-search__results') || results;
            const visibleStatus = visibleModal.querySelector('.lm-live-search__status') || status;
            if (!json || !json.success) {
                render(visibleResults, { groups: [], query: trimmed }, emptyMessage);
            } else {
                render(visibleResults, json.data, emptyMessage);
            }
            if (visibleStatus) visibleStatus.textContent = '';
        })
        .catch((error) => {
            if (error && error.name === 'AbortError') return;
            render(results, { groups: [], query: trimmed }, emptyMessage);
            if (status) status.textContent = '';
        });
}

function initDelegation() {
    document.addEventListener('input', (event) => {
        const input = event.target.closest && event.target.closest('.lm-live-search__input');
        if (!input) return;
        syncResetButton(input);
        const value = input.value;
        if (state.timer) clearTimeout(state.timer);
        state.timer = setTimeout(() => run(value, false), DEBOUNCE_MS);
    });

    document.addEventListener('keydown', (event) => {
        const input = event.target.closest && event.target.closest('.lm-live-search__input');
        if (!input) return;
        if (event.key === 'Escape') {
            resetSearch(input);
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            run(input.value, true);
        }
    });

    document.addEventListener('submit', (event) => {
        const form = event.target.closest && event.target.closest('.lm-live-search');
        if (!form) return;
        event.preventDefault();
    });

    document.addEventListener('click', (event) => {
        const reset = event.target.closest && event.target.closest('.button--reset');
        if (reset) {
            event.preventDefault();
            const form = reset.closest('.lm-live-search');
            const input = form && form.querySelector('.lm-live-search__input');
            if (input) resetSearch(input);
            return;
        }

        const badge = event.target.closest && event.target.closest('.lm-live-search__badge');
        if (!badge) return;
        event.preventDefault();
        const next = badge.dataset.scope || 'alle';
        if (next === state.scope) return;
        state.scope = next;

        const modal = badge.closest('.header-search-modal') || document;
        syncBadges(modal);

        const input = modal.querySelector('.lm-live-search__input');
        const value = input ? input.value.trim() : '';
        const config = window.lmSearch;
        const minChars = config && Number(config.minChars) > 0 ? Number(config.minChars) : 2;
        if (value.length >= minChars) {
            run(value, true);
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDelegation);
} else {
    initDelegation();
}
